'use client';

import * as React from 'react';
import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    ChevronRight,
    ChevronLeft,
    Check,
    Settings2,
    Layout,
    Calendar,
    FileText,
    Users,
    User,
    Sparkles,
    Rocket,
    CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
    useCreateExternalProjectMutation,
    useCreateInternalProjectMutation
} from '@/state';
import { useTenant } from '@/hooks';
import type { PROJECT_TYPE } from '@/types';

interface ProjectWizardProps {
    open: boolean;
    defaultType?: PROJECT_TYPE;
    onOpenChange: (open: boolean) => void;
    onComplete: (type?: PROJECT_TYPE) => void;
    onSkipToStandard?: () => void;
    onCreated?: (projectId: number) => Promise<void> | void;
}

interface WizardState {
    type: PROJECT_TYPE | null;
    name: string;
    durationDays: number;
    project_structure: 1 | 2; // 1 = Group, 2 = Individual
}

export const ProjectWizard: React.FC<ProjectWizardProps> = ({
    open,
    defaultType,
    onOpenChange,
    onComplete,
    onSkipToStandard,
    onCreated,
}) => {
    const { tenant } = useTenant();
    const [createInternal] = useCreateInternalProjectMutation();
    const [createExternal] = useCreateExternalProjectMutation();

    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [state, setState] = useState<WizardState>({
        type: defaultType || null,
        name: '',
        durationDays: 30,
        project_structure: defaultType === 'INTERNAL' ? 1 : 2,
    });

    const isInternal = state.type === 'INTERNAL';
    const displaySteps = isInternal ? [1, 2, 4] : [1, 2, 3];
    const currentStepIndex = displaySteps.indexOf(step) + 1;
    const totalDisplaySteps = displaySteps.length;

    const handleNext = () => {
        if (step === 2 && isInternal) {
            setStep(4);
        } else if (step === 2) {
            setStep(3);
        } else {
            setStep((s) => Math.min(s + 1, isInternal ? 4 : 3));
        }
    };

    const handleBack = () => {
        if (step === 4 && isInternal) {
            setStep(2);
        } else if (step === 3 && !isInternal) {
            setStep(2);
        } else {
            setStep((s) => Math.max(s - 1, 1));
        }
    };

    const handleCreate = async () => {
        if (!state.type || !state.name || !tenant) return;

        setIsSubmitting(true);
        try {
            if (state.type === 'INTERNAL') {
                const createdProject = await createInternal({
                    tenant,
                    data: {
                        name: state.name,
                        project_duration: state.durationDays,
                        project_structure: 1, // Internal is always 1
                        project_type: null,
                    },
                }).unwrap();
                await onCreated?.(createdProject.project_id ?? createdProject.id);
            } else {
                const createdProject = await createExternal({
                    tenant,
                    data: {
                        name: state.name,
                        project_duration: state.durationDays,
                        project_structure: 2,
                        project_type: 2,
                    },
                }).unwrap();
                await onCreated?.(createdProject.project_id ?? createdProject.id);
            }
            toast.success('Project created successfully!');
            onComplete(state.type || undefined);
            onOpenChange(false);
            // Reset state
            setStep(1);
            setState({
                type: defaultType || null,
                name: '',
                durationDays: 30,
                project_structure: defaultType === 'INTERNAL' ? 1 : 2,
            });
        } catch (error) {
            toast.error('Failed to create project');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="space-y-6 py-2 animate-in fade-in slide-in-from-right-8 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <button
                                onClick={() => setState({ ...state, type: 'INTERNAL', project_structure: 1 })}
                                className={cn(
                                    "flex flex-col items-center gap-4 md:gap-5 p-5 md:p-8 rounded-[24px] md:rounded-[32px] border-2 transition-all duration-300 group relative overflow-hidden active:scale-[0.98]",
                                    state.type === 'INTERNAL'
                                        ? "border-primary bg-primary/5 ring-4 ring-primary/10 shadow-xl shadow-primary/10"
                                        : "border-border/50 bg-background hover:border-primary/30 hover:bg-muted/30"
                                )}
                            >
                                <div className={cn(
                                    "w-14 h-14 md:w-18 md:h-18 rounded-[18px] md:rounded-[22px] flex items-center justify-center transition-all duration-500 shadow-lg",
                                    state.type === 'INTERNAL'
                                        ? "bg-primary text-primary-foreground shadow-primary/30 scale-110 rotate-3"
                                        : "bg-muted text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                                )}>
                                    <Layout className="w-7 h-7 md:w-9 md:h-9" />
                                </div>
                                <div className="text-center">
                                    <h3 className="font-bold text-base md:text-lg tracking-tight text-foreground">Internal</h3>
                                    <p className="text-xs md:text-sm text-muted-foreground mt-1.5 text-pretty leading-relaxed">Managed projects for internal teams and cohorts.</p>
                                </div>
                                {state.type === 'INTERNAL' && (
                                    <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-primary text-primary-foreground rounded-full p-1 shadow-lg">
                                        <Check className="w-3 h-3 md:w-4 md:h-4" />
                                    </div>
                                )}
                                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                            </button>

                            <button
                                onClick={() => setState({ ...state, type: 'EXTERNAL' })}
                                className={cn(
                                    "flex flex-col items-center gap-4 md:gap-5 p-5 md:p-8 rounded-[24px] md:rounded-[32px] border-2 transition-all duration-300 group relative overflow-hidden active:scale-[0.98]",
                                    state.type === 'EXTERNAL'
                                        ? "border-primary bg-primary/5 ring-4 ring-primary/10 shadow-xl shadow-primary/10"
                                        : "border-border/50 bg-background hover:border-primary/30 hover:bg-muted/30"
                                )}
                            >
                                <div className={cn(
                                    "w-14 h-14 md:w-18 md:h-18 rounded-[18px] md:rounded-[22px] flex items-center justify-center transition-all duration-500 shadow-lg",
                                    state.type === 'EXTERNAL'
                                        ? "bg-primary text-primary-foreground shadow-primary/30 scale-110 -rotate-3"
                                        : "bg-muted text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                                )}>
                                    <Settings2 className="w-7 h-7 md:w-9 md:h-9" />
                                </div>
                                <div className="text-center">
                                    <h3 className="font-bold text-base md:text-lg tracking-tight text-foreground">External</h3>
                                    <p className="text-xs md:text-sm text-muted-foreground mt-1.5 text-pretty leading-relaxed">Collaborative projects with external partners.</p>
                                </div>
                                {state.type === 'EXTERNAL' && (
                                    <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-primary text-primary-foreground rounded-full p-1 shadow-lg">
                                        <Check className="w-3 h-3 md:w-4 md:h-4" />
                                    </div>
                                )}
                                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                            </button>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-5 md:space-y-6 py-4 animate-in fade-in slide-in-from-right-8 duration-500">
                        <div className="space-y-2">
                            <label className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2 mb-1">
                                <FileText className="w-3.5 h-3.5 text-primary" />
                                Identity
                            </label>
                            <Input
                                placeholder="e.g. Design System Phase 1"
                                value={state.name}
                                onChange={(e) => setState({ ...state, name: e.target.value })}
                                className="text-base md:text-xl py-5 md:py-6 rounded-[12px] md:rounded-[16px] border-2 border-muted focus-visible:ring-primary/20 focus-visible:border-primary/50 transition-all font-semibold placeholder:text-muted-foreground/30"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2 mb-1">
                                <Calendar className="w-3.5 h-3.5 text-primary" />
                                Timeline
                            </label>
                            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 p-4 md:p-5 bg-muted/30 rounded-[16px] md:rounded-[20px] border-2 border-transparent hover:border-muted-foreground/10 transition-all">
                                <Input
                                    type="number"
                                    value={state.durationDays}
                                    onChange={(e) => setState({ ...state, durationDays: parseInt(e.target.value) || 30 })}
                                    className="w-full md:w-28 text-center text-lg md:text-xl py-5 md:py-6 rounded-[10px] md:rounded-[12px] border-2 border-muted focus-visible:ring-primary/20 font-bold"
                                />
                                <div className="space-y-0.5 text-center md:text-left">
                                    <span className="text-base md:text-lg font-semibold block text-foreground">Days</span>
                                    <span className="text-[11px] md:text-xs text-muted-foreground font-medium">Estimated time to complete this journey.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-5 md:space-y-6 py-4 animate-in fade-in zoom-in-95 duration-500">
                        <div className="bg-muted/30 rounded-[20px] md:rounded-[28px] p-5 md:p-7 space-y-3.5 md:space-y-5 border-2 border-muted/50 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-6 md:p-10 opacity-5 -rotate-12 pointer-events-none text-foreground">
                                <Rocket className="w-28 h-28 md:w-40 md:h-40" />
                            </div>

                            <div className="grid gap-3.5 md:gap-5 relative z-10">
                                <div className="flex items-center justify-between group">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Deployment</span>
                                    <Badge className="bg-primary/10 text-primary border-none px-3 py-0.5 font-bold text-[10px] md:text-xs tracking-wide rounded-full">{state.type}</Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Identity</span>
                                    <span className="font-bold text-sm md:text-lg text-foreground tracking-tight line-clamp-1">{state.name}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Duration</span>
                                    <span className="font-bold text-xs md:text-base text-foreground bg-foreground/5 px-2.5 py-1 rounded-lg">{state.durationDays} Days</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Structure</span>
                                    <div className="flex items-center gap-2 font-bold text-xs md:text-base text-foreground">
                                        {isInternal ? <Users className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 text-primary" /> : <User className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 text-primary" />}
                                        {isInternal ? 'Collaborative' : 'Solo'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isInternal && (
                            <div className="p-3.5 md:p-4 rounded-[14px] md:rounded-[18px] bg-blue-500/10 text-blue-700 text-[10px] md:text-xs border border-blue-500/20 flex gap-3 items-center">
                                <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 text-blue-600" />
                                </div>
                                <p className="font-medium leading-snug">
                                    <strong>Note:</strong> Internal projects are fixed as <strong>Collaborative (Group)</strong> to ensure team alignment.
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col items-center justify-center p-3 md:p-5 text-center space-y-2 md:space-y-3">
                            <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-yellow-500/10 flex items-center justify-center animate-bounce">
                                <Sparkles className="w-5 h-5 md:w-7 md:h-7 text-yellow-500" />
                            </div>
                            <div className="space-y-0.5">
                                <h4 className="text-base md:text-lg font-bold tracking-tight text-foreground">Ready for blast-off?</h4>
                                <p className="text-[11px] md:text-xs font-medium text-muted-foreground">Everything looks perfect. Proceed to launch your project.</p>
                            </div>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-5 md:space-y-6 py-4 animate-in fade-in zoom-in-95 duration-500">
                        <div className="bg-muted/30 rounded-[20px] md:rounded-[28px] p-5 md:p-7 space-y-3.5 md:space-y-5 border-2 border-muted/50 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-6 md:p-10 opacity-5 -rotate-12 pointer-events-none text-foreground">
                                <Rocket className="w-28 h-28 md:w-40 md:h-40" />
                            </div>

                            <div className="grid gap-3.5 md:gap-5 relative z-10">
                                <div className="flex items-center justify-between group">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Deployment</span>
                                    <Badge className="bg-primary/10 text-primary border-none px-3 py-0.5 font-bold text-[10px] md:text-xs tracking-wide rounded-full">{state.type}</Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Identity</span>
                                    <span className="font-bold text-sm md:text-lg text-foreground tracking-tight line-clamp-1">{state.name}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Duration</span>
                                    <span className="font-bold text-xs md:text-base text-foreground bg-foreground/5 px-2.5 py-1 rounded-lg">{state.durationDays} Days</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Structure</span>
                                    <div className="flex items-center gap-2 font-bold text-xs md:text-base text-foreground">
                                        {state.project_structure === 1 ? <Users className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 text-primary" /> : <User className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 text-primary" />}
                                        {state.project_structure === 1 ? 'Collaborative' : 'Solo'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isInternal && (
                            <div className="p-3.5 md:p-4 rounded-[14px] md:rounded-[18px] bg-blue-500/10 text-blue-700 text-[10px] md:text-xs border border-blue-500/20 flex gap-3 items-center">
                                <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 text-blue-600" />
                                </div>
                                <p className="font-medium leading-snug">
                                    <strong>Note:</strong> Internal projects are fixed as <strong>Collaborative (Group)</strong> to ensure team alignment.
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col items-center justify-center p-3 md:p-5 text-center space-y-2 md:space-y-3">
                            <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-yellow-500/10 flex items-center justify-center animate-bounce">
                                <Sparkles className="w-5 h-5 md:w-7 md:h-7 text-yellow-500" />
                            </div>
                            <div className="space-y-0.5">
                                <h4 className="text-base md:text-lg font-bold tracking-tight text-foreground">Ready for blast-off?</h4>
                                <p className="text-[11px] md:text-xs font-medium text-muted-foreground">Everything looks perfect. Proceed to launch your project.</p>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const isNextDisabled = () => {
        if (step === 1) return !state.type;
        if (step === 2) return !state.name.trim() || state.durationDays <= 0;
        return false;
    };

    const getStepTitle = () => {
        switch (step) {
            case 1: return "Choose project path";
            case 2: return "Project fundamentals";
            case 3: return "Final review";
            default: return "Create Project";
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[620px] w-[95vw] md:w-full overflow-hidden rounded-[24px] md:rounded-[36px] p-0 border-none shadow-2xl bg-background/90 backdrop-blur-3xl ring-1 ring-white/10 flex flex-col max-h-[85vh]">
                <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-5 md:p-8 border-b relative shrink-0">
                    <div className="flex items-center justify-between mb-3 md:mb-5">
                        <Badge className="bg-primary text-primary-foreground font-bold tracking-widest px-3 py-1 rounded-full shadow-lg shadow-primary/20 text-[9px] md:text-[10px]">
                            STEP {currentStepIndex} / {totalDisplaySteps}
                        </Badge>
                        <div className="flex gap-1.5 md:gap-2">
                            {displaySteps.map((s, i) => (
                                <div
                                    key={s}
                                    className={cn(
                                        "h-1 md:h-1.5 rounded-full transition-all duration-500 ease-spring",
                                        i < currentStepIndex ? "bg-primary w-6 md:w-10" : "bg-muted w-1.5 md:w-2"
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                    <DialogHeader className="text-left">
                        <DialogTitle className="text-xl md:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/60 leading-tight">
                            {getStepTitle()}
                        </DialogTitle>
                        <DialogDescription className="text-xs md:text-base font-medium text-muted-foreground/80 mt-1">
                            A seamless journey to launch your next big idea.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="px-5 md:px-10 py-2 md:py-4 flex-1 overflow-y-auto custom-scrollbar">
                    {renderStep()}
                </div>

                <div className="p-4 md:p-6 bg-muted/40 border-t flex items-center justify-between backdrop-blur-md shrink-0">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            onClick={handleBack}
                            disabled={step === 1 || isSubmitting}
                            className="rounded-[12px] md:rounded-[16px] px-4 md:px-7 py-3 md:py-5 font-semibold text-muted-foreground hover:text-foreground transition-all active:scale-95 text-xs md:text-sm"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 mr-1.5 md:mr-2.5" />
                            Back
                        </Button>

                        {onSkipToStandard && (
                            <Button
                                variant="ghost"
                                onClick={onSkipToStandard}
                                disabled={isSubmitting}
                                className="rounded-[12px] md:rounded-[16px] px-4 md:px-7 py-3 md:py-5 font-semibold text-primary/70 hover:text-primary hover:bg-primary/5 transition-all text-xs md:text-sm underline underline-offset-4"
                            >
                                Skip to Standard Mode
                            </Button>
                        )}
                    </div>

                    {step === (isInternal ? 4 : 3) ? (
                        <Button
                            onClick={handleCreate}
                            disabled={isSubmitting}
                            className="rounded-[16px] md:rounded-[20px] px-7 md:px-10 py-5 md:py-7 font-bold text-sm md:text-lg shadow-xl shadow-primary/30 bg-primary hover:bg-primary/90 transition-all active:scale-95 transform hover:scale-[1.02]"
                        >
                            {isSubmitting ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-3.5 h-3.5 md:w-4.5 md:h-4.5 border-[2px] border-background border-t-transparent rounded-full animate-spin" />
                                    Deploying...
                                </div>
                            ) : (
                                <>
                                    <Rocket className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2.5" />
                                    Launch
                                </>
                            )}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            disabled={isNextDisabled()}
                            className="rounded-[16px] md:rounded-[20px] px-7 md:px-10 py-5 md:py-7 font-bold text-sm md:text-lg shadow-xl shadow-primary/30 transition-all active:scale-95 transform hover:scale-[1.02]"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 ml-1.5 md:ml-2.5" />
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
